import { type DonationStatus, hasReceivedNoMoney, type PaymentStatus } from './donation-status.js';
import { type FormField, formFieldEmptyValue, formFieldShownValue } from './form-schema.js';
import type { Permission } from './permissions.js';

/**
 * How much of a donation a correction may still change —
 * docs/decisions/0118-a-donation-is-edited-until-its-work-begins-and-money-locks-only-money.md.
 *
 * - `full`: every field, the figures included.
 * - `nonFinancial`: everything but what prices the donation or moves money —
 *   `DONATION_FINANCIAL_FIELDS` and the answers `lockedAnswerKeys` names.
 * - `none`: nothing; the form is read-only for this editor.
 */
export const DONATION_EDIT_MODES = ['full', 'nonFinancial', 'none'] as const;
export type DonationEditMode = (typeof DONATION_EDIT_MODES)[number];

/**
 * Why a correction is narrowed, or null when it is not.
 *
 * - `needs_edit`: no `donation.edit`.
 * - `cancelled`: the pledge has been given back; nobody corrects it.
 * - `queued`: in the order, without `donation.editQueued` or `donation.editLocked`.
 * - `listed`: on an active PDF list, without `donation.editLocked` — the paper
 *   is in a field team's hands.
 * - `fulfilled`: carried out. Without `donation.editLocked` read-only; with it,
 *   its figures and the answers that group or place it stay as they are.
 * - `money_received`: money has reached it, so its figures stay as a match holds them.
 */
export const DONATION_EDIT_LOCKS = [
  'needs_edit',
  'cancelled',
  'queued',
  'listed',
  'fulfilled',
  'money_received',
] as const;
export type DonationEditLock = (typeof DONATION_EDIT_LOCKS)[number];

/**
 * What the work already done on a donation holds still, whoever corrects it
 * and in whichever mode — the 0118 amendment.
 *
 * - `partly_fulfilled`: some of its shares are recorded in a carried-out
 *   animal, so its quantity, period, yurt and the answers that price, place or
 *   group it stay as that animal was carried out with.
 * - `on_list`: an active PDF list holds it, and the list names its yurt and
 *   season; the yurt and the period stay until it leaves the list.
 * - `receipt_issued`: an issued receipt names its donor; the payer stays until
 *   the receipt is voided and reissued.
 */
export const DONATION_EDIT_HOLDS = ['partly_fulfilled', 'on_list', 'receipt_issued'] as const;
export type DonationEditHold = (typeof DONATION_EDIT_HOLDS)[number];

export type DonationEditability = {
  readonly mode: DonationEditMode;
  readonly reason: DonationEditLock | null;
  /** Empty when `mode` is `none`: nothing is taken, so nothing more is held. */
  readonly holds: readonly DonationEditHold[];
};

export type DonationEditRow = {
  readonly status: DonationStatus;
  readonly paymentStatus: PaymentStatus;
  /** Whether an active PDF list holds the donation — `printout_list_donations.released_at IS NULL`. */
  readonly listed: boolean;
  /** Its shares recorded in carried-out animals — `fulfilled_queue_group_shares`. */
  readonly recordedShares: number;
  /** Whether an issued, unvoided receipt names it. */
  readonly receiptIssued: boolean;
};

/**
 * The one answer the detail page, the edit form, the service and the writer
 * under the row's `FOR UPDATE` lock all read. `donation.editLocked` is held by
 * the supervisors and never derived from a staff assignment; it opens a
 * queued, listed or fulfilled donation, but a fulfilled one only for what
 * neither prices it nor groups it. The money gate narrows to the figures alone.
 */
export function donationEditability(
  row: DonationEditRow,
  editor: { readonly permissions: readonly Permission[] },
): DonationEditability {
  const narrowed = editModeOf(row, editor.permissions);
  return { ...narrowed, holds: narrowed.mode === 'none' ? [] : editHoldsOf(row) };
}

function editModeOf(
  row: DonationEditRow,
  permissions: readonly Permission[],
): Omit<DonationEditability, 'holds'> {
  const holds = (permission: Permission) => permissions.includes(permission);
  if (!holds('donation.edit')) return { mode: 'none', reason: 'needs_edit' };
  if (row.status === 'cancelled') return { mode: 'none', reason: 'cancelled' };
  const unlocks = holds('donation.editLocked');
  if (row.status === 'fulfilled') {
    return unlocks
      ? { mode: 'nonFinancial', reason: 'fulfilled' }
      : { mode: 'none', reason: 'fulfilled' };
  }
  if (row.listed && !unlocks) return { mode: 'none', reason: 'listed' };
  if (row.status === 'queued' && !unlocks && !holds('donation.editQueued')) {
    return { mode: 'none', reason: 'queued' };
  }
  if (!hasReceivedNoMoney(row.paymentStatus))
    return { mode: 'nonFinancial', reason: 'money_received' };
  return { mode: 'full', reason: null };
}

/** A fulfilled donation already holds more than a partial one would, so it is not named twice. */
function editHoldsOf(row: DonationEditRow): DonationEditHold[] {
  const holds: DonationEditHold[] = [];
  if (row.recordedShares > 0 && row.status !== 'fulfilled') holds.push('partly_fulfilled');
  if (row.listed) holds.push('on_list');
  if (row.receiptIssued) holds.push('receipt_issued');
  return holds;
}

/**
 * The donation's own columns a `nonFinancial` correction may not move: what it
 * costs, in what, how many, for which season and day — the day freezes the
 * exchange rate — and the yurt, which carries a unit cost (0115).
 */
export const DONATION_FINANCIAL_FIELDS = [
  'amountMinor',
  'currency',
  'quantity',
  'campaignPeriodId',
  'donatedAt',
  'fulfillmentInstitutionId',
] as const;
export type DonationFinancialField = (typeof DONATION_FINANCIAL_FIELDS)[number];

export type CampaignAnswerKeys = {
  readonly priceFieldKeys: readonly string[];
  readonly capabilityFieldKeys: readonly string[];
  readonly groupByFieldKeys: readonly string[];
};

/**
 * The campaign answers a correction in this mode may not change. A price
 * answer re-prices; a capability answer decides which yurt may carry it out,
 * and so its cost. On a fulfilled donation the grouping answers are fixed too,
 * because its animal or group was carried out with them.
 */
export function lockedAnswerKeys(
  editability: DonationEditability,
  campaign: CampaignAnswerKeys,
): string[] {
  if (editability.mode === 'none') return [];
  const keys = new Set<string>();
  if (editability.mode === 'nonFinancial') {
    for (const key of pricingKeys(campaign)) keys.add(key);
  }
  if (editability.reason === 'fulfilled' || editability.holds.includes('partly_fulfilled')) {
    for (const key of [...pricingKeys(campaign), ...campaign.groupByFieldKeys]) keys.add(key);
  }
  return [...keys].sort();
}

function pricingKeys(campaign: CampaignAnswerKeys): string[] {
  return [...campaign.priceFieldKeys, ...campaign.capabilityFieldKeys];
}

/**
 * What a correction may change of a donation besides its figures and answers,
 * when a hold keeps it: the payer, and the yurt, period and quantity a
 * `full` correction would otherwise move.
 */
export const DONATION_HELD_FIELDS = [
  'quantity',
  'campaignPeriodId',
  'fulfillmentInstitutionId',
  'donorId',
] as const;
export type DonationHeldField = (typeof DONATION_HELD_FIELDS)[number];

const HELD_BY: Record<DonationEditHold, readonly DonationHeldField[]> = {
  partly_fulfilled: ['quantity', 'campaignPeriodId', 'fulfillmentInstitutionId'],
  on_list: ['campaignPeriodId', 'fulfillmentInstitutionId'],
  receipt_issued: ['donorId'],
};

/** The fields the holds keep, in `DONATION_HELD_FIELDS` order. */
export function heldEditFields(editability: DonationEditability): DonationHeldField[] {
  const held = new Set(editability.holds.flatMap((hold) => HELD_BY[hold]));
  return DONATION_HELD_FIELDS.filter((field) => held.has(field));
}

export type DonationCorrectionRefusal =
  | 'needs_edit'
  | 'cancelled'
  | 'queued'
  | 'listed'
  | 'fulfilled'
  | 'money_received_financial'
  | 'fulfilled_financial'
  | 'fulfilled_grouping'
  | DonationEditHold;

export type DonationCorrection = {
  readonly financialFields: readonly DonationFinancialField[];
  readonly answerKeys: readonly string[];
  /** Whether the payer — `donor_id` — moves. */
  readonly donorChanged?: boolean;
};

/**
 * Whether a correction that moves these things is taken, or the refusal. A
 * `nonFinancial` editability refuses a financial column or a price or
 * capability answer — on a fulfilled donation as `fulfilled_financial`,
 * otherwise `money_received_financial` — and on a fulfilled one a grouping
 * answer as `fulfilled_grouping`. Then each hold refuses what it keeps, under
 * its own name, in every mode but `none`.
 */
export function donationCorrectionRefusal(
  editability: DonationEditability,
  change: DonationCorrection,
  campaign: CampaignAnswerKeys,
): DonationCorrectionRefusal | null {
  if (editability.mode === 'none') {
    return editability.reason === 'money_received' || editability.reason === null
      ? 'needs_edit'
      : editability.reason;
  }
  if (editability.mode === 'nonFinancial') {
    const fulfilled = editability.reason === 'fulfilled';
    const pricing = new Set(pricingKeys(campaign));
    if (change.financialFields.length > 0 || change.answerKeys.some((key) => pricing.has(key))) {
      return fulfilled ? 'fulfilled_financial' : 'money_received_financial';
    }
    if (fulfilled && change.answerKeys.some((key) => campaign.groupByFieldKeys.includes(key))) {
      return 'fulfilled_grouping';
    }
  }
  return heldMoved(editability, change, campaign);
}

function heldMoved(
  editability: DonationEditability,
  change: DonationCorrection,
  campaign: CampaignAnswerKeys,
): DonationEditHold | null {
  const moved = new Set<DonationHeldField>(
    DONATION_HELD_FIELDS.filter((field) =>
      (change.financialFields as readonly string[]).includes(field),
    ),
  );
  if (change.donorChanged) moved.add('donorId');
  for (const hold of editability.holds) {
    if (HELD_BY[hold].some((field) => moved.has(field))) return hold;
    if (hold !== 'partly_fulfilled') continue;
    const held = new Set([...pricingKeys(campaign), ...campaign.groupByFieldKeys]);
    if (change.answerKeys.some((key) => held.has(key))) return hold;
  }
  return null;
}

/**
 * The answer keys whose value differs between two `form_data` documents. With
 * the form's fields, each side is read as the edit form handles it. A stored
 * answer is what `initialValues` in `packages/ui` shows, by the shared
 * `formFieldShownValue` rule: only a missing key is the field's default, or its
 * type's empty value; a stored null or empty string is shown blank, so picking
 * the default over it is a change. A sent answer is what the form submits,
 * which drops a blank input: a missing key there is the type's empty value. A
 * key missing on both sides is unchanged. A key no field names is compared as
 * stored.
 */
export function changedAnswerKeys(
  before: unknown,
  after: unknown,
  fields: readonly FormField[] = [],
): string[] {
  const a = answersOf(before);
  const b = answersOf(after);
  const byKey = new Map(fields.map((field) => [field.key, field]));
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].filter((key) => !sameAnswer(byKey.get(key), key, a, b)).sort();
}

/**
 * `after` with every answer `changedAnswerKeys` reads as unchanged put back as
 * `before` stored it — absent where it was absent — so a correction that
 * changed nothing of an answer writes nothing of it.
 */
export function keepUnchangedAnswers(
  before: unknown,
  after: Record<string, unknown>,
  fields: readonly FormField[],
): Record<string, unknown> {
  const a = answersOf(before);
  const kept: Record<string, unknown> = { ...after };
  for (const field of fields) {
    if (!sameAnswer(field, field.key, a, after)) continue;
    if (field.key in a) kept[field.key] = a[field.key];
    else delete kept[field.key];
  }
  return kept;
}

function sameAnswer(
  field: FormField | undefined,
  key: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): boolean {
  if (!field) return JSON.stringify(before[key]) === JSON.stringify(after[key]);
  if (!(key in before) && !(key in after)) return true;
  const shown = blankAsEmpty(field, formFieldShownValue(field, before[key]));
  return JSON.stringify(shown) === JSON.stringify(blankAsEmpty(field, after[key]));
}

function blankAsEmpty(field: FormField, value: unknown): unknown {
  return value === undefined || value === null || value === '' ? formFieldEmptyValue(field) : value;
}

function answersOf(formData: unknown): Record<string, unknown> {
  return formData !== null && typeof formData === 'object' && !Array.isArray(formData)
    ? (formData as Record<string, unknown>)
    : {};
}
